import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, publicItemImage, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserRound, Loader2, RefreshCw, Tag, Settings2 } from "lucide-react";
import { toast } from "sonner";

/** Small pill that opens the twin try-on. Only shown for pieces that fit the viewer. */
export const TryOnButton = ({ item, className = "", onClick }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
    className={`inline-flex items-center gap-1 rounded-full bg-foreground text-background px-2.5 h-7 text-[11px] font-medium shadow-soft hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors ${className}`}
    title="This fits you - see it on your twin" data-testid={`tryon-open-${item.id}`}>
    <UserRound className="h-3 w-3" /> On my twin
  </button>
);

/**
 * "See it on my twin": renders a market / social piece on the viewer's own twin, styled with their own go-to pieces.
 * Explicit tap only (credits are spent once per piece and cached); shows the cached render straight away when there is one.
 */
export const TryOnDialog = ({ item, open, onOpenChange, onOffer }) => {
  const [state, setState] = useState("idle"); // idle | loading | done | error | no_twin
  const [paired, setPaired] = useState([]);
  const [bust, setBust] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let on = true;
    setState("checking");
    api.tryOnStatus(item.id).then((s) => {
      if (!on) return;
      if (s.status === "done") { setPaired(s.paired_with || []); setBust(Date.now()); setState("done"); }
      else setState("idle");
    }).catch(() => on && setState("idle"));
    return () => { on = false; };
  }, [open, item.id]);

  const render = async (force = false) => {
    setState("loading"); setErr("");
    try {
      const r = await api.tryOnRender(item.id, force);
      setPaired(r.paired_with || []); setBust(Date.now()); setState("done");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't dress your twin right now";
      if (e?.response?.status === 400 && /twin/i.test(msg)) { setState("no_twin"); return; }
      setErr(msg); toast.error(msg); setState("error");
    }
  };

  const price = (item.listing_type === "sell" || item.listing_type === "both") && item.listing_price != null ? money(item.listing_price) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="tryon-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">See it on your twin</DialogTitle>
          <DialogDescription>{item.name}{item.user?.handle ? ` · from @${item.user.handle}` : ""}{item.size ? ` · ${item.size}, your size` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[112px_1fr] sm:grid-cols-[160px_1fr] gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">The piece</p>
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white border border-border"><img src={publicItemImage(item)} alt={item.name} className="h-full w-full object-contain" /></div>
            {price && <p className="text-sm font-mono-plex font-semibold mt-2" data-testid="tryon-price">{price}</p>}
            {paired.length > 0 && state === "done" && <p className="text-[11px] text-muted-foreground mt-2" data-testid="tryon-paired">Styled with your {paired.join(" and ")}.</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">On you</p>
            <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-secondary" data-testid={`tryon-stage-${state}`}>
              {(state === "idle") && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4">
                  <UserRound className="h-7 w-7 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground max-w-xs">We'll put it on your twin with your own go-to pieces so you can see the whole look before you make an offer.</p>
                  <Button onClick={() => render(false)} className="rounded-full" data-testid="tryon-render-button"><UserRound className="h-4 w-4 mr-1.5" /> Dress my twin</Button>
                </div>
              )}
              {(state === "loading" || state === "checking") && (
                <>
                  <Skeleton className="absolute inset-0" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" /> {state === "loading" ? "Dressing your twin…" : "Checking…"}
                  </div>
                </>
              )}
              {state === "done" && <img src={`${api.tryOnImageUrl(item.id)}&t=${bust}`} alt={`${item.name} on your twin`} className="absolute inset-0 h-full w-full object-cover" data-testid="tryon-image" />}
              {state === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground px-4 text-center">
                  {err || "Couldn't render the look"}
                  <Button size="sm" variant="secondary" className="rounded-full" onClick={() => render(false)} data-testid="tryon-retry"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again</Button>
                </div>
              )}
              {state === "no_twin" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4" data-testid="tryon-no-twin">
                  <Settings2 className="h-6 w-6 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Set up your twin first - a photo of you is all it takes.</p>
                  <Button asChild size="sm" className="rounded-full"><Link to="/settings" data-testid="tryon-go-settings">Set up my twin</Link></Button>
                </div>
              )}
              {state === "done" && (
                <button type="button" onClick={() => render(true)} className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium hover:bg-background" title="Render again (spends credits)" data-testid="tryon-rerender"><RefreshCw className="h-3 w-3" /> Again</button>
              )}
            </div>
          </div>
        </div>
        {onOffer && (
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="tryon-close">Close</Button>
            <Button onClick={() => { onOpenChange(false); onOffer(); }} className="rounded-full" data-testid="tryon-make-offer"><Tag className="h-4 w-4 mr-1.5" /> Make an offer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
